import React from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import { 
  DataGridPro, 
  GridColDef, 
  GridColumnVisibilityModel, 
  GridEventListener, 
  GridRenderCellParams, 
  GridRowId, 
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { isEmpty } from 'lodash';
import { useAPI } from 'contexts/APIProvider'
import { PreferredNurseSearchForm } from 'types/interfaces'
import { Observable } from 'rxjs';
import PreferredNurse from 'logic/PreferredNurse';
import { Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { adminNurseDetailURL, adminPracticeDetailURL } from 'routes/urls';

interface AdminPreferredNurseTableProps {
  reload$?: Observable<boolean>
  onRowClick?: GridEventListener<"rowClick">
  actions?: React.ReactNode
  form: PreferredNurseSearchForm
  updateForm: (name: string, value: any) => void
  selection: GridRowId[]
  setSelection: (value: GridRowId[]) => void
  columnVisibilityModel: GridColumnVisibilityModel
  setColumnVisibilityModel: (value: GridColumnVisibilityModel) => void
  sortModel: GridSortModel
  setSortModel: (value: GridSortModel) => void
}

const AdminPreferredNurseTable: React.FC<AdminPreferredNurseTableProps> = ({
  reload$,
  onRowClick,
  actions,
  form,
  updateForm,
  selection,
  setSelection,
  columnVisibilityModel,
  setColumnVisibilityModel,
  sortModel,
  setSortModel,
}) => {
  const { api } = useAPI()
  const [preferredNurses, setPreferredNurses] = React.useState<PreferredNurse[]>([])
  const [loading, setLoading] = React.useState<boolean>(false)

  const fetchPreferredNurses = React.useCallback(async () => {
    setLoading(true)
    setPreferredNurses(await api.listPreferredNurses(form))
    setLoading(false)
  }, [api, form])

  const onSelectionChanged = React.useCallback((selectedIDs: GridRowId[]) => {
    setSelection(selectedIDs)
  }, [setSelection])

  React.useEffect(() => {
    fetchPreferredNurses()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    const sub = reload$?.subscribe(() => fetchPreferredNurses())
    return () => sub?.unsubscribe()
  }, [fetchPreferredNurses, reload$])
  
  const columns: GridColDef<PreferredNurse>[] = React.useMemo(() => [{
    minWidth: 50,
    headerName: 'ID',
    field: 'id',
    type: 'number',
  }, {
    minWidth: 200,
    headerName: 'Nurse',
    field: 'nurse_name',
    type: 'string',
    renderCell: (params: GridRenderCellParams<PreferredNurse>) => {
      const preferredNurse = params.row
      return (
        <Link typography='body1' component={RouterLink} to={adminNurseDetailURL(preferredNurse.nurse_id)}>
          {preferredNurse.nurse_name} {preferredNurse.nurse_enabled ? '' : '(Disabled)'}
        </Link>
      )
    }
  }, {
    minWidth: 200,
    headerName: 'Practice',
    field: 'practice_name',
    type: 'string',
    renderCell: (params: GridRenderCellParams<PreferredNurse>) => {
      const preferredNurse = params.row
      return (
        <Link typography='body1' component={RouterLink} to={adminPracticeDetailURL(preferredNurse.practice_id)}>
          {preferredNurse.practice_name}
        </Link>
      )
    }
  }, {
    minWidth: 200,
    headerName: 'Practice Group',
    field: 'practice_group_name',
    type: 'string',
    renderCell: (params: GridRenderCellParams<PreferredNurse>) => {
      const preferredNurse = params.row
      return preferredNurse.practice_group_name || 'Independent'
    }
  }, {
    minWidth: 100,
    headerName: 'Tier',
    field: 'nurse_tier',
    type: 'string',
  }, {
    minWidth: 100,
    headerName: 'Role',
    field: 'nurse_role',
    type: 'string',
  }], [])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
      {loading ? (
        <Alert severity='info'>Please Wait ...</Alert>
      ) : isEmpty(preferredNurses) ? (
        <Alert severity='info'>No Records Yet</Alert>
      ) : (
        <React.Fragment>
          <Alert severity='info'>{preferredNurses.length} records returned. {selection.length} selected</Alert>
          <DataGridPro 
            rows={preferredNurses}
            columns={columns}
            getRowId={(preferredNurse) => preferredNurse.id}
            checkboxSelection   
            disableRowSelectionOnClick
            rowSelectionModel={selection}
            onRowSelectionModelChange={onSelectionChanged}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
            sortModel={sortModel}
            onSortModelChange={(newModel) => setSortModel(newModel)}
            loading={loading}
            onRowDoubleClick={onRowClick}
            hideFooter
          />
        </React.Fragment>
      )}
    </Box>
  )
}

export default AdminPreferredNurseTable